import { useCallback, useLayoutEffect, useRef, useState, type PointerEvent as EventoPuntero } from 'react';

/**
 * Arrastrar filas con el asa ⠿ en «Personalizar menú» (componentes.md §1g): Pointer Events (ratón, dedo o lápiz; sirve en
 * iPad Safari), la fila se recoloca en vivo y se suelta en la zona y delante de la fila que quede bajo el puntero. Las
 * zonas llevan `data-rv-zona` y las filas `data-rv-fila`; mientras se arrastra no se guarda nada: al soltar, una sola
 * llamada a `onSoltar`. ↑ ↓ y quitar o devolver son la alternativa de teclado y táctil.
 */
export interface Arrastre {
    clave: string;
    zona: string;
    /** Fila delante de la que va; null: al final de la zona. */
    antes: string | null;
}

export function useArrastre(onSoltar: (clave: string, zona: string, antes: string | null) => void) {
    const [arrastre, setArrastre] = useState<Arrastre | null>(null);
    const soltar = useRef(onSoltar);
    useLayoutEffect(() => {
        soltar.current = onSoltar;
    });

    const empezar = useCallback((clave: string, zona: string, e: EventoPuntero) => {
        if (e.pointerType === 'mouse' && e.button !== 0) {
            return;
        }
        e.preventDefault();
        let actual: Arrastre = { clave, zona, antes: null };
        setArrastre(actual);
        const mueve = (ev: PointerEvent) => {
            const bajo = document.elementFromPoint(ev.clientX, ev.clientY);
            const caja = bajo?.closest<HTMLElement>('[data-rv-zona]') ?? null;
            const nueva = caja?.dataset.rvZona;
            if (caja === null || nueva === undefined) {
                return;
            }
            let antes: string | null = null;
            for (const fila of caja.querySelectorAll<HTMLElement>('[data-rv-fila]')) {
                const k = fila.dataset.rvFila ?? '';
                if (k === clave) {
                    continue;
                }
                const r = fila.getBoundingClientRect();
                if (ev.clientY < r.top + r.height / 2) {
                    antes = k;
                    break;
                }
            }
            if (nueva !== actual.zona || antes !== actual.antes) {
                actual = { clave, zona: nueva, antes };
                setArrastre(actual);
            }
        };
        const fin = () => {
            window.removeEventListener('pointermove', mueve);
            window.removeEventListener('pointerup', fin);
            window.removeEventListener('pointercancel', fin);
            setArrastre(null);
            soltar.current(actual.clave, actual.zona, actual.antes);
        };
        window.addEventListener('pointermove', mueve);
        window.addEventListener('pointerup', fin);
        window.addEventListener('pointercancel', fin);
    }, []);

    return { arrastre, empezar };
}

/**
 * Las filas de cada zona como se ven mientras se arrastra: la arrastrada sale de la suya y entra en la de destino,
 * delante de `antes`, si `admite` deja soltarla allí.
 */
export function previsualizar<Z extends { clave: string; filas: readonly { clave: string }[] }>(zonas: readonly Z[], arrastre: Arrastre | null, admite: (zona: string) => boolean): readonly Z[] {
    if (arrastre === null || !admite(arrastre.zona)) {
        return zonas;
    }
    const fila = zonas.flatMap((z) => z.filas).find((f) => f.clave === arrastre.clave);
    if (fila === undefined) {
        return zonas;
    }
    return zonas.map((z) => {
        const sin = z.filas.filter((f) => f.clave !== arrastre.clave);
        if (z.clave !== arrastre.zona) {
            return { ...z, filas: sin };
        }
        const i = arrastre.antes === null ? -1 : sin.findIndex((f) => f.clave === arrastre.antes);
        return { ...z, filas: [...sin.slice(0, i < 0 ? sin.length : i), fila, ...sin.slice(i < 0 ? sin.length : i)] };
    });
}
